import {
  useQuery,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'api';
import {
  OrganizationUpgrade,
  OrganizationUpgradeData,
} from 'features/organizations/organizations.types';

type UseOrganizationUpgradeProps = {
  previewOrganizationUpgrade?: OrganizationUpgradeData;
};

type UseOrganizationUpgrade = Omit<
  UseQueryResult<OrganizationUpgrade, Error>,
  'data'
> & {
  previewUpgrade?: OrganizationUpgrade;
  processOrganizationUpgrade: (
    organizationUpgradeData: OrganizationUpgradeData,
  ) => Promise<OrganizationUpgrade>;
};

const fetchOrganizationUpgradePreview = async (
  data: OrganizationUpgradeData,
): Promise<OrganizationUpgrade> => {
  const response = await API.post('organization/upgrade/preview/', data);
  return response.data;
};

const processUpgrade = async (
  data: OrganizationUpgradeData,
): Promise<OrganizationUpgrade> => {
  const response = await API.post('organization/upgrade/', data);
  return response.data;
};

export const useOrganizationUpgrade = (
  props: UseOrganizationUpgradeProps,
): UseOrganizationUpgrade => {
  const { previewOrganizationUpgrade: organizationUpgradeData } = props;
  const queryClient = useQueryClient();

  const { data: previewUpgrade, ...results } = useQuery<OrganizationUpgrade>({
    queryKey: [
      'organizationUpgradePreview',
      {
        regularSeats: organizationUpgradeData?.regularSeats || 0,
        proSeats: organizationUpgradeData?.proSeats || 0,
      },
    ],
    queryFn: () => fetchOrganizationUpgradePreview(organizationUpgradeData!),
    staleTime: 60 * 1000, // 1 minute
    enabled:
      Boolean(organizationUpgradeData) &&
      (organizationUpgradeData?.regularSeats || 0) +
        (organizationUpgradeData?.proSeats || 0) >
        0,
  });

  const processUpgradeMutation = useMutation<
    OrganizationUpgrade,
    Error,
    OrganizationUpgradeData
  >({
    mutationFn: processUpgrade,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organizationDetails'],
      });
    },
  });

  const processOrganizationUpgrade = async (
    data: OrganizationUpgradeData,
  ): Promise<OrganizationUpgrade> => {
    const response = await processUpgradeMutation.mutateAsync(data);
    return response;
  };

  return {
    ...results,
    previewUpgrade,
    processOrganizationUpgrade,
  };
};
